import React, { useEffect, useMemo, useState } from "react";

import {
  DeleteFilled,
  DownloadOutlined,
  EditFilled,
  FileFilled,
  HomeOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper } from "@mui/material";
import { Button, Input, Pagination, Space, Table, Tag } from "antd";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import Select from "react-select";

import { CheckCircle } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useAddFamilyMemberMutation,
  useUpdateFamilyMemberMutation,
  useCreatePatientMutation,
  useGetallergiesChronicDeaseseQuery,
  useGetmedicationSupplementQuery,
  useGetPatientListQuery,
} from "../../../utils/apiSlice";
import { readPrimaryUserData } from "../../../utils/activeProfileStorage";
import { tryEarnUserAction } from "../../../utils/gamificationService";
import { hydrateGamification } from "../../../utils/gamificationSlice";
import { useEffectiveUserData } from "../../../hooks/useEffectiveUserData";
import { emailValidation, nameValidation, phoneValidation } from "../../../utils/validation";
import {
  formatHeightFtInInput,
  sanitizeHeightFtInTyping,
  validateHeightCm,
  validateHeightFtIn,
  validateWeightKg,
  validateWeightLbs,
} from "../../../utils/heightWeightFormat";
import * as Yup from "yup";
import { triggerCoins } from "../../canvasConfict";

const PatientFamilyMember = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedConditions, setSelectedConditions] = useState<any[]>([]);
  const [selectedMedications, setSelectedMedications] = useState<any[]>([]);
  const [medicationSearch, setMedicationSearch] = useState("");
  const [addFamilyMember, { isLoading }] = useAddFamilyMemberMutation();
  const [updateFamilyMember, { isLoading: isUpdateLoading }] = useUpdateFamilyMemberMutation();
  const [createPatient, { isLoading: isPostLoading }] = useCreatePatientMutation();
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [selectedFamilyHistory, setSelectedFamilyHistory] = useState<any[]>([]);
  const [supplementSearch, setSupplementSearch] = useState("");

  const [familyList, setFamilyList] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [allergySearch, setAllergySearch] = useState("");
  const [selectedSupplements, setSelectedSupplements] = useState<any[]>([]);
  const [selectedAllergies, setSelectedAllergies] = useState<any[]>([]);
  const [familySearch, setFamilySearch] = useState("");
  const primaryUser = readPrimaryUserData();
  /** Jis profile par switch kiya hai — family list usi user ki relationships dikhaegi */
  const effectiveUser = useEffectiveUserData();
  const listOwnerUserId =
    effectiveUser?.userId ?? primaryUser?.userId ?? primaryUser?.id;

  const { data: patientsData, refetch: refetchPatientList } = useGetPatientListQuery(
    listOwnerUserId,
    { skip: !listOwnerUserId }
  );

  useEffect(() => {
    if (!patientsData?.data) return;

    const mappedFamily = patientsData.data.map((member: any, index: number) => {
      const cleanValue = (val: any) =>
        val === "null" || val === null || val === undefined ? "N/A" : val;

      const hasPatientId = !!member?.patientId;
      const questionnairePending = !hasPatientId;

      /** Spread API row so edit uses list data only (no extra GET). Display fields override where needed. */
      return {
        ...member,
        key: member.relatedUserId ?? index,
        questionnairePending,

        name: member.fullName || `${member.firstName || ""} ${member.lastName || ""}`.trim(),
        relation: member.relationshipType || "N/A",
        dob: cleanValue(member.dateOfBirth),

        gender: cleanValue(member.gender),
        bloodGroup: cleanValue(member.bloodGroup),

        phone: cleanValue(member.phoneNumber),
        email: cleanValue(member.email),

        status: "Active",
      };
    });

    setFamilyList(mappedFamily);
  }, [patientsData]);

  const [units, setUnits] = useState({ height: 'cm', weight: 'kg' });
  const [conditionSearch, setConditionSearch] = useState("");
  const { data: conditionResults } = useGetallergiesChronicDeaseseQuery(conditionSearch, {
    skip: conditionSearch.length === 1,
  });

  const [formData, setFormData] = useState<any>({
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    bloodGroup: "",
    status: true,
    relationshipType: "SPOUSE",
    gender: "MALE",
    sexAssignedAtBirth: "MAN",
    phoneNumber: "",
    email: ""
  });


  const handleInputChange = (e: any) => {
    const { name, value } = e.target;


    if (["firstName", "middleName", "lastName"].includes(name)) {
      // Only alphabets allowed: no spaces, numbers, or special symbols
      const regex = /^[A-Za-z]*$/;

      if (!regex.test(value)) {
        setErrors((prev: any) => ({
          ...prev,
          [name]: "Only alphabets are allowed. Spaces, numbers, and special characters are not allowed.",
        }));
        return;
      } else {
        setErrors((prev: any) => ({
          ...prev,
          [name]: "",
        }));
      }
    }


    if (name === "phoneNumber") {
      const regex = /^[0-9]*$/;

      if (!regex.test(value)) {
        setErrors((prev: any) => ({
          ...prev,
          phoneNumber: "Only numbers are allowed",
        }));
        return;
      } else {
        setErrors((prev: any) => ({
          ...prev,
          phoneNumber: "",
        }));
      }
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };



  const validate = () => {
    let newErrors: any = {};

    try { nameValidation.validateSync(formData.firstName); } catch (e: any) { newErrors.firstName = e.message; }
    try { nameValidation.validateSync(formData.lastName); } catch (e: any) { newErrors.lastName = e.message; }

    if (!formData.relationshipType)
      newErrors.relationshipType = "Relation is required";

    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of Birth is required";

    if (!formData.gender)
      newErrors.gender = "Assigned at Birth is required";

    if (!formData.bloodGroup)
      newErrors.bloodGroup = "Blood Group is required";

    if (formData.email) {
      try { emailValidation.validateSync(formData.email); } catch (e: any) { newErrors.email = e.message; }
    }

    if (formData.phoneNumber) {
      try { phoneValidation.validateSync(formData.phoneNumber); } catch (e: any) { newErrors.phoneNumber = e.message; }
    }

    if (formData.status === undefined || formData.status === null)
      newErrors.status = "Status is required";


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const allowOnlyCharacters = (e: any) => {
    const regex = /^[A-Za-z]$/;

    if (!regex.test(e.key)) {
      e.preventDefault();
    }
  };
  const handleSaveFamilyMember = async () => {
    if (!validate()) return;

    try {
      const payload: any = {
        firstName: formData.firstName.trim(),
        middleName: formData.middleName?.trim() || "",
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth,
        bloodGroup: formData.bloodGroup || null,
        status: formData.status,
        relationshipType: formData.relationshipType || "FRIEND",
        gender: formData.gender || "MALE",
        phoneNumber: formData.phoneNumber || null,
        email: formData.email,
        sexAssignedAtBirth: formData.sexAssignedAtBirth,
      };

      const fullName = `${payload.firstName} ${payload.middleName} ${payload.lastName}`
        .replace(/\s+/g, " ")
        .trim();
      payload.fullName = fullName;

      let response: any;

      if (editingRecord) {
        const relatedUserId = editingRecord.relatedUserId;
        if (relatedUserId == null || relatedUserId === "") {
          toast.error("Cannot update: missing related user id.");
          return;
        }

        const relationshipId = editingRecord.relationshipId;
        if (relationshipId == null || relationshipId === "") {
          toast.error("Cannot update: missing relationship id.");
          return;
        }

        /** PUT user-relationships/{relatedUserId} — payload from form + flags from row */
        const updateBody = {
          relationshipId,
          relationshipType: String(
            formData.relationshipType || editingRecord.relationshipType || "FRIEND"
          ).toUpperCase(),
          firstName: formData.firstName.trim(),
          middleName: formData.middleName?.trim() ? formData.middleName.trim() : null,
          lastName: formData.lastName.trim(),
          dateOfBirth: formData.dateOfBirth,
          phoneNumber: formData.phoneNumber?.trim() ? formData.phoneNumber.trim() : null,
          bloodGroup: formData.bloodGroup || null,
          isPrimary: Boolean(editingRecord.isPrimary),
          canView: editingRecord.canView !== false,
          canEdit: editingRecord.canEdit !== false,
          canManageConsent: editingRecord.canManageConsent !== false,
             email: formData.email,
        };

        response = await updateFamilyMember({
          relatedUserId,
          ...updateBody,
        }).unwrap();
      } else {
        const addBody =
          effectiveUser?._isViewingRelatedProfile && listOwnerUserId
            ? { ...payload, primaryUserId: listOwnerUserId }
            : payload;
        response = await addFamilyMember(addBody).unwrap();

      }

      if (response?.success || response) {
        const newMemberData = response.data || response;

        if (!editingRecord) {
          const updatedRawList = JSON.parse(
            localStorage.getItem("familyMembers") || "[]"
          );

          if (response?.data?.coinSummary?.coinsEarned) {
            triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

          }

          const newRawMember = {
            firstName: payload.firstName,
            middleName: payload.middleName,
            lastName: payload.lastName,
            fullName,
            dateOfBirth: payload.dateOfBirth,
            gender: payload.gender,
            bloodGroup: payload.bloodGroup,
            isPrimary: newMemberData.isPrimary,
            primaryUserId: newMemberData.primaryUserId,
            relatedUserEmail: newMemberData.relatedUserEmail,
            relatedUserId: newMemberData.relatedUserId,
            relationshipId: newMemberData.relationshipId,
            relationshipType: newMemberData.relationshipType,
          };

          updatedRawList.push(newRawMember);
          localStorage.setItem(
            "familyMembers",
            JSON.stringify(updatedRawList)
          );

          setUserId(newMemberData.relatedUserId);
          setShowConfirm(true);
        }

        const isGenericSuccess = (s: unknown) =>
          typeof s !== "string" || !s.trim() || /^success\.?$/i.test(s.trim());

        const apiDataStr =
          typeof response?.data === "string" ? response.data.trim() : "";
        const apiMessageStr =
          typeof response?.message === "string" ? response.message.trim() : "";

        const toastMsg = editingRecord
          ? !isGenericSuccess(apiDataStr)
            ? apiDataStr
            : !isGenericSuccess(apiMessageStr)
              ? apiMessageStr
              : "Family Member updated successfully."
          : !isGenericSuccess(apiDataStr)
            ? apiDataStr
            : !isGenericSuccess(apiMessageStr)
              ? apiMessageStr
              : "Family Member added successfully.";

        toast.success(toastMsg);
        if (!editingRecord) {
          const owner = readPrimaryUserData();
          const ownerId = owner?.userId ?? owner?.id;
          if (ownerId) {
            const coin = tryEarnUserAction(String(ownerId), "family_member_added");
            if (coin.ok) {
              dispatch(hydrateGamification(String(ownerId)));
            }
          }
        }
        setShowModal(false);
        setEditingRecord(null);
        refetchPatientList();
      }
    } catch (error: any) {
      let errorMsg = editingRecord ? "Failed to update family member" : "Failed to add family member";

      if (typeof error?.data === "string") {
        errorMsg = error.data;
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      } else if (error?.data?.detail) {
        errorMsg = error.data.detail;
      } else if (error?.message) {
        errorMsg = error.message;
      } else if (error?.error === "TypeError: Failed to fetch") {
        errorMsg =
          "Network Error: Failed to connect. Please try again later.";
      }

      toast.error(errorMsg);
    }
  };

  const [questionnaireData, setQuestionnaireData] = useState<any>({
    firstName: "SS",
    middleName: "SS",
    lastName: "SS",
    bloodGroup: "",
    dateOfBirth: "",
    gender: null,
    sexAssignedAtBirth: null,
    height: "",
    weight: "",
    currentLocation: "",
    emergencyContactName: "",
    emergencyRelationship: "",
    emergencyPhoneNumber: "",
    emergencyContactConsent: null,
    diagnosedConditionIds: [],
    allergyIds: [],
    currentlyTakingMedications: null,
    patientMedicationIds: [],
    supplementIds: [],
    familyHistoryDiseaseIds: [],
    goals: "",
    finalInput: "",
  });

  const formatDecimal = (value: number) =>
    Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");

  const inferHeightUnit = (value: any) => String(value || "").includes("'") ? "ft/in" : "cm";
  const inferWeightUnit = (value: any) => String(value || "").toLowerCase().includes("lb") ? "lbs" : "kg";

  const cmToFeetInches = (value: any) => {
    const cm = Number(value);
    if (!value || Number.isNaN(cm)) return "";
    const totalInches = Math.round(cm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}'${inches}"`;
  };

  const feetInchesToCm = (value: any) => {
    const match = String(value).match(/(\d+)\s*'\s*(\d{1,2})"?/);
    if (!match) return "";
    const feet = Number(match[1]);
    const inches = Number(match[2]);
    return String(Math.round((feet * 12 + inches) * 2.54));
  };

  const kgToLbs = (value: any) => {
    const kg = Number(value);
    if (!value || Number.isNaN(kg)) return "";
    return formatDecimal(kg * 2.20462);
  };

  const lbsToKg = (value: any) => {
    const lbs = Number(value);
    if (!value || Number.isNaN(lbs)) return "";
    return formatDecimal(lbs / 2.20462);
  };

  const normalizeHeightInput = (value: string, unit: string) =>
    unit === "cm" ? value.replace(/[^0-9]/g, "") : sanitizeHeightFtInTyping(value);

  const sanitizeWeightInput = (value: string) =>
    value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");

  const handleHeightUnitChange = (nextUnit: "cm" | "ft/in") => {
    if (nextUnit === units.height) return;
    setQuestionnaireData((prev: any) => ({
      ...prev,
      height: nextUnit === "ft/in" ? cmToFeetInches(prev.height) : feetInchesToCm(prev.height),
    }));
    setUnits((prev) => ({ ...prev, height: nextUnit }));
  };

  const handleWeightUnitChange = (nextUnit: "kg" | "lbs") => {
    if (nextUnit === units.weight) return;
    setQuestionnaireData((prev: any) => ({
      ...prev,
      weight: nextUnit === "lbs" ? kgToLbs(prev.weight) : lbsToKg(prev.weight),
    }));
    setUnits((prev) => ({ ...prev, weight: nextUnit }));
  };

  const openQuestionnaireModal = (record: any) => {
    const normalize = (value: any) =>
      value === "N/A" || value === "null" || value === null || value === undefined
        ? ""
        : value;

    setQuestionnaireData({
      firstName: normalize(record?.firstName),
      middleName: normalize(record?.middleName),
      lastName: normalize(record?.lastName),
      bloodGroup: normalize(record?.bloodGroup),
      dateOfBirth: normalize(record?.dateOfBirth || record?.dob),
      gender: normalize(record?.gender),
      sexAssignedAtBirth: normalize(record?.sexAssignedAtBirth),
      height: "",
      weight: "",
      currentLocation: "",
      emergencyContactName: "",
      emergencyRelationship: "",
      emergencyPhoneNumber: "",
      emergencyContactConsent: null,
      diagnosedConditionIds: [],
      allergyIds: [],
      currentlyTakingMedications: null,
      patientMedicationIds: [],
      supplementIds: [],
      familyHistoryDiseaseIds: [],
      goals: "",
      finalInput: "",
    });

    setSelectedConditions([]);
    setSelectedAllergies([]);
    setSelectedMedications([]);
    setSelectedSupplements([]);
    setSelectedFamilyHistory([]);
    setErrors({});
    setActiveStep(0);
    setUnits({
      height: inferHeightUnit(record?.height),
      weight: inferWeightUnit(record?.weight),
    });
    setUserId(record?.key || 0);
    setShowModal1(true);
  };



  const handleFamilyChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };



  const [showModal, setShowModal] = useState(false);
  const [showModal1, setShowModal1] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  React.useEffect(() => {
    if (!showModal1) return;

    const onKeyDownCapture = (ev: KeyboardEvent) => {
      const isSpace =
        ev.key === " " || ev.code === "Space" || (ev as any).key === "Spacebar";
      if (!isSpace) return;

      const menu = document.querySelector(".react-select__menu .react-select__option");
      if (!menu) return;

      ev.preventDefault();
      ev.stopImmediatePropagation();
    };

    document.addEventListener("keydown", onKeyDownCapture, true);
    return () => document.removeEventListener("keydown", onKeyDownCapture, true);
  }, [showModal1]);

  const [errors, setErrors] = useState<any>({});

  const handleQChange = (field: string, value: any) => {
    setQuestionnaireData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    }
  };
  const getAgeInMonths = (dob: any) => {
    if (!dob) return null;

    const birth = new Date(dob);
    const today = new Date();

    let months =
      (today.getFullYear() - birth.getFullYear()) * 12 +
      (today.getMonth() - birth.getMonth());

    return months;
  };
  const convertFtInToCm = (value: any) => {
    if (!value) return null;

    const match = value.match(/(\d+)[^\d]+(\d+)/); // e.g. 5'10
    if (!match) return null;

    const ft = parseInt(match[1] || 0);
    const inch = parseInt(match[2] || 0);

    return ft * 30.48 + inch * 2.54;
  };
  const validateStep = (step: number) => {
    const q = questionnaireData;
    let newErrors: any = {};

    if (step === 0) {
      try { nameValidation.validateSync(q.firstName); } catch (e: any) { newErrors.firstName = e.message; }
      try { nameValidation.validateSync(q.lastName); } catch (e: any) { newErrors.lastName = e.message; }
      if (!q.dateOfBirth) newErrors.dateOfBirth = "Date of Birth is required";
      if (!q.gender) newErrors.gender = "Gender identity is required";
      if (!q.sexAssignedAtBirth) newErrors.sexAssignedAtBirth = "Sex assigned at birth is required";
      const ageInMonths = getAgeInMonths(q.dateOfBirth);
      const heightValue = String(q?.height || "").trim();
      if (!heightValue) {
        newErrors.height = "Height is required";
      } else {
        let height =
          units.height === "cm"
            ? parseFloat(q.height)
            : convertFtInToCm(q.height);


        if (ageInMonths !== null && ageInMonths <= 1) {
          if (!height || height < 45 || height > 60) {
            newErrors.height = "For newborn, height must be between 45–60 cm";
          }
        } else {

          const hErr =
            units.height === "cm"
              ? validateHeightCm(String(q.height))
              : validateHeightFtIn(String(q.height));

          if (hErr) newErrors.height = hErr;
        }
      }

      const weightValue = String(q?.weight || "").trim();
      if (!weightValue) {
        newErrors.weight = "Weight is required";
      } else {
        let weight = parseFloat(q.weight);


        if (units.weight === "lbs") {
          weight = weight * 0.453592;
        }


        if (ageInMonths !== null && ageInMonths <= 1) {
          if (!weight || weight < 2 || weight > 5) {
            newErrors.weight = "For newborn, weight must be between 2–5 kg";
          }
        } else {

          const wErr =
            units.weight === "kg"
              ? validateWeightKg(String(q.weight))
              : validateWeightLbs(String(q.weight));

          if (wErr) newErrors.weight = wErr;
        }
      }
      const dob = q.dateOfBirth;
      const minDate = "1926-01-01";
      const maxDate = new Date().toISOString().split("T")[0];

      if (!dob) {
        newErrors.dateOfBirth = "Date of Birth is required";
      } else if (dob < minDate) {
        newErrors.dateOfBirth = "Date cannot be before 01-01-1926";
      } else if (dob > maxDate) {
        newErrors.dateOfBirth = "Future dates are not allowed";
      }

      if (!q.currentLocation.trim()) newErrors.currentLocation = "Location is required";
      if (!q.bloodGroup.trim()) newErrors.bloodGroup = "Blood Group is required";
    } else if (step === 1) {
      try { nameValidation.validateSync(q.emergencyContactName); } catch (e: any) { newErrors.emergencyContactName = e.message; }
      if (!q.emergencyRelationship.trim()) newErrors.emergencyRelationship = "Relationship is required";

      try { phoneValidation.validateSync(q.emergencyPhoneNumber); } catch (e: any) { newErrors.emergencyPhoneNumber = e.message; }

      if (!q.emergencyContactConsent) newErrors.emergencyContactConsent = "Consent is required";
    } else if (step === 3) {
      if (!q.currentlyTakingMedications) {
        newErrors.currentlyTakingMedications = "Please select an option";
      }
      if (
        q.currentlyTakingMedications === "true" &&
        (!q.patientMedicationIds || q.patientMedicationIds.length === 0)
      ) {
        newErrors.patientMedicationIds = "Please list your medications";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveQuestionnaire = async () => {
    if (validateStep(activeStep)) {
      try {



        const payload = {
          ...questionnaireData,
          emergencyContactConsent:
            questionnaireData.emergencyContactConsent === "true",

          currentlyTakingMedications:
            questionnaireData.currentlyTakingMedications === "true",
          userId: userId
        };

        const response: any = await createPatient(payload).unwrap();
        const patientId1 = response?.data?.patientId;
        if (response?.data?.coinSummary?.coinsEarned) {
          triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

        }

        let stored = readPrimaryUserData();

        if (stored) {
          stored.patientId = patientId1;

          if (localStorage.getItem("userData")) {
            localStorage.setItem("userData", JSON.stringify(stored));
          } else {
            sessionStorage.setItem("userData", JSON.stringify(stored));
          }
        }
        const toastMsg = (typeof response?.data === "string" && response.data) || response?.message || "Patient updated successfully.";
        toast.success(toastMsg);
        // Refresh family table so "Questionnare Pending" updates immediately.
        await refetchPatientList();
        setShowModal1(false);
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to save questionnaire");
      }
    }
  };

  const normalizeRelationshipType = (r: any) => {
    if (r == null || r === "" || r === "N/A") return "";
    return String(r).trim().toUpperCase().replace(/\s+/g, "_");
  };

  const fillFormFromListRecord = (record: any) => {
    const dob =
      record.dateOfBirth ||
      (record.dob && record.dob !== "N/A" ? record.dob : "");
    const bg =
      record.bloodGroup && record.bloodGroup !== "N/A" ? record.bloodGroup : "";
    const phone =
      record.phoneNumber != null && record.phoneNumber !== ""
        ? String(record.phoneNumber)
        : record.phone === "N/A"
          ? ""
          : String(record.phone || "");

    setFormData({
      firstName: record.firstName || "",
      middleName: record.middleName ?? "",
      lastName: record.lastName || "",
      dateOfBirth: dob,
      bloodGroup: bg,
      relationshipType:
        normalizeRelationshipType(record.relationshipType || record.relation) ||
        "SPOUSE",
      phoneNumber: phone,
      email:
        record.email != null && record.email !== "N/A"
          ? String(record.email)
          : "",
      gender:
        record.gender && record.gender !== "N/A" ? record.gender : "MALE",
      sexAssignedAtBirth: record.sexAssignedAtBirth || "",
      status: true,
    });
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    setErrors({});
    fillFormFromListRecord(record);
    setShowModal(true);
  };

  const steps = [
    "Basic Info",
    "Emergency",
    "Health Overview",
    "Medications",
    "Family History",
    "Goals",
    "Final Input",
  ];




  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }

  };

  const mergeOptions = (selected: any[] = [], apiOptions: any[] = []) => {
    const map = new Map<string, any>();

    const keyOf = (item: any) => {
      const id = item?.id ?? item?.value ?? item?.name ?? item?.parentName ?? item?.relationshipType ?? item?.relationship;
      return id === undefined || id === null || id === "" ? null : String(id).trim().toUpperCase();
    };

    [...selected, ...apiOptions].forEach((item) => {
      const key = keyOf(item);
      if (!key) return;
      const normalized = { ...item, id: item?.id ?? item?.name ?? item?.parentName ?? item?.value };
      map.set(key, normalized);
    });

    return Array.from(map.values());
  };
  const { data: supplementResults } = useGetmedicationSupplementQuery(supplementSearch, {
    skip: supplementSearch.length === 1,
  });
  const supplementApiOptions =
    supplementResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
      descriptions: item.name,
    })) || [];

  const supplementOptions = mergeOptions(selectedSupplements, supplementApiOptions);


  const conditionApiOptions =
    conditionResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || [];


  const conditionOptions = mergeOptions(selectedConditions, conditionApiOptions);



  const { data: allergyResults } = useGetallergiesChronicDeaseseQuery(allergySearch, {
    skip: allergySearch.length === 1,
  });
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const filteredFamilyList = useMemo(() => {
    if (!searchText.trim()) return familyList;
    const lower = searchText.toLowerCase();
    return familyList.filter((row: any) =>
      [row.name, row.relation, row.bloodGroup, row.phone, row.email]
        .some((val) => val && String(val).toLowerCase().includes(lower))
    );
  }, [familyList, searchText]);

  const dataSource = filteredFamilyList;
  const paginatedDataSource = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return dataSource.slice(start, start + pageSize);
  }, [dataSource, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(dataSource.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [dataSource.length, pageSize, currentPage]);

  const allergyApiOptions =
    allergyResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || [];

  const allergyOptions = mergeOptions(selectedAllergies, allergyApiOptions);

  const sexOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    // { value: "INTERSEX", label: "Intersex" },
    { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    // { value: "NON_BINARY", label: "Non-binary" },
    // { value: "TRANSGENDER_MAN", label: "Transgender man" },
    // { value: "TRANSGENDER_WOMAN", label: "Transgender woman" },
    // { value: "OTHER", label: "Different identity (write in)" },
    { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
  ];

  const { data: medicationResults } = useGetmedicationSupplementQuery(medicationSearch, {
    skip: medicationSearch.length === 1,
  });
  const medicationApiOptions =
    medicationResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
      descriptions: item.name,
    })) || [];

  const medicationOptions = mergeOptions(selectedMedications, medicationApiOptions);


  const [userId, setUserId] = useState(0)
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: any, record: any) => (
        <div>
          <div>{text}</div>
          {record.questionnairePending && (
            <span
              className="blinker-badge"
              onClick={() => openQuestionnaireModal(record)}
            >
              Questionnare Pending
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Patient ID",
      dataIndex: "patientId",
      key: "patientId",
      render: (patientId: string | null, record: any) =>
        patientId ? (
          patientId
        ) : (
          <span className="pending-blink" onClick={() => openQuestionnaireModal(record)}>Pending</span>
        ),
    },


    {
      title: "Relation",
      dataIndex: "relation",
      key: "relation",
    },
    {
      title: "Date of Birth",
      dataIndex: "dob",
      key: "dob",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (gender: any) => (
        <Tag color={gender === "Male" ? "green" : "red"}>
          {gender}
        </Tag>
      ),
    },
    {
      title: "Blood Group",
      dataIndex: "bloodGroup",
      key: "bloodGroup",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: any) =>
        status === "Active" ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <Space>
          <Button

            shape="circle"
            icon={<EditFilled />}
            className="btn-edit"
            onClick={() => handleEdit(record)}
          />
          {/* <Button
            shape="circle"
            icon={<DeleteFilled />}
            className="btn-delete"
          /> */}

        </Space>
      ),
    },
  ];




  const yesNoOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];
  const { data: familyResults } = useGetallergiesChronicDeaseseQuery(familySearch, {
    skip: familySearch.length === 1,
  });



  const familyApiOptions =
    familyResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || [];

  const familyOptions = mergeOptions(selectedFamilyHistory, familyApiOptions);

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "38px",
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 999999999 }),
  };

  const relationshipOptions = [
    { value: "SPOUSE", label: "Spouse" },
    { value: "PARENT", label: "Parent" },
    { value: "CHILD", label: "Child" },
    { value: "SIBLING", label: "Sibling" },
    { value: "FRIEND", label: "Friend" },
    { value: "OTHER", label: "Other" },
  ];

  const stepContents = [
    // Step 0 - Basic Info
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">First Name <span className="text-danger">*</span></label>
        <input
          type="text"
          className="form-control"
          placeholder="First Name"
          value={questionnaireData.firstName ? questionnaireData.firstName.charAt(0).toUpperCase() + questionnaireData.firstName.slice(1).toLowerCase() : ""}
          readOnly
          style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
        />
        {errors.firstName && <p className="text-danger mt-1 mb-0 fs-12">{errors.firstName}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Middle Name</label>
        <input
          type="text"
          className="form-control"
          placeholder="Middle Name"
          value={questionnaireData.middleName ? questionnaireData.middleName.charAt(0).toUpperCase() + questionnaireData.middleName.slice(1).toLowerCase() : ""}
          readOnly
          style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Last Name <span className="text-danger">*</span></label>
        <input
          type="text"
          className="form-control"
          placeholder="Last Name"
          value={questionnaireData.lastName ? questionnaireData.lastName.charAt(0).toUpperCase() + questionnaireData.lastName.slice(1).toLowerCase() : ""}
          readOnly
          style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
        />
        {errors.lastName && <p className="text-danger mt-1 mb-0 fs-12">{errors.lastName}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
        <input
          type="date"
          className="form-control"
          value={questionnaireData.dateOfBirth}
          min="1926-01-01"
          max={new Date().toISOString().split("T")[0]}

          onChange={(e) => {
            const val = e.target.value;


            handleQChange("dateOfBirth", val);


            const minDate = "1926-01-01";
            const maxDate = new Date().toISOString().split("T")[0];
            let errorMsg = "";

            if (!val) {
              errorMsg = "Date of Birth is required";
            } else if (val < minDate) {
              errorMsg = "Date cannot be before 01-01-1926";
            } else if (val > maxDate) {
              errorMsg = "Future dates are not allowed";
            }

            setErrors((prev: any) => ({ ...prev, dateOfBirth: errorMsg }));
          }}

        />
        {errors.dateOfBirth && <p className="text-danger mt-1 mb-0 fs-12">{errors.dateOfBirth}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Gender identity <span className="text-danger">*</span></label>
        <Select
          styles={selectStyles}
          classNamePrefix="react-select"
          options={genderIdentityOptions}
          value={genderIdentityOptions.find(o => o.value === questionnaireData.gender)}
          onChange={(val: any) => handleQChange('gender', val?.value || "")}
          placeholder="Select"
          menuPortalTarget={document.body}
        />
        {errors.gender && <p className="text-danger mt-1 mb-0 fs-12">{errors.gender}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Sex assigned at birth <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
           styles={selectStyles}
          options={sexOptions}
          value={sexOptions.find(o => o.value === questionnaireData.sexAssignedAtBirth)}
          onChange={(val: any) => handleQChange('sexAssignedAtBirth', val?.value || "")}
          placeholder="Select"
          menuPortalTarget={document.body}
        />
        {errors.sexAssignedAtBirth && <p className="text-danger mt-1 mb-0 fs-12">{errors.sexAssignedAtBirth}</p>}
      </div>

      {/* Height Field */}
      <div className="col-md-6">
        <div className="d-flex justify-content-between align-items-end mb-2">
          <label className="form-label mb-0">Height <span className="text-danger">*</span></label>
          <div className="unit-toggle-container">
            <button
              type="button"
              className={`unit-btn ${units.height === 'cm' ? 'active' : ''}`}
              onClick={() => handleHeightUnitChange("cm")}
            >CM</button>
            <button
              type="button"
              className={`unit-btn ${units.height === 'ft/in' ? 'active' : ''}`}
              onClick={() => handleHeightUnitChange("ft/in")}
            >FT/IN</button>
          </div>
        </div>
        <div className="input-group">
          <input
            disabled={!questionnaireData.dateOfBirth}
            type="text"
            className="form-control"
            placeholder={units.height === 'cm' ? "180" : "Type e.g. 60 → 6'0\""}
            value={questionnaireData.height}
            onChange={(e) => {
              const rawValue = e.target.value;

              let value =
                units.height === "cm"
                  ? rawValue.replace(/[^0-9]/g, "")
                  : sanitizeHeightFtInTyping(rawValue);


              if (units.height === "cm") {
                if (value && parseInt(value) > 300) {
                  value = "300"; // clamp to max
                }
              }

              handleQChange("height", value);


              const ageInMonths = getAgeInMonths(questionnaireData.dateOfBirth);

              let height = null;

              if (units.height === "cm") {
                height = parseFloat(value);
              } else {
                height = convertFtInToCm(value);
              }

              let errorMsg = "";

              if (ageInMonths !== null && ageInMonths <= 1) {
                if (!value) {
                  errorMsg = "Height is required";
                } else if (!height || height < 45 || height > 60) {
                  errorMsg = "For newborn, height must be between 45–60 cm";
                }
              }

              setErrors((prev: any) => ({
                ...prev,
                height: errorMsg,
              }));
            }}
            onBlur={(e) => {
              if (units.height !== "ft/in") return;
              const v = e.target.value.trim();
              if (!v) return;
              const next = formatHeightFtInInput(v);
              if (next && next !== e.target.value) {
                handleQChange("height", next);
              }
            }}
          />
          <span className="input-group-text fs-12 text-muted" style={{ width: '52px', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
            {units.height === "cm" ? "cm" : "ft/in"}
          </span>
        </div>
        {errors.height && <p className="text-danger mt-1 mb-0 fs-12">{errors.height}</p>}
      </div>

      {/* Weight Field */}
      <div className="col-md-6">
        <div className="d-flex justify-content-between align-items-end mb-2">
          <label className="form-label mb-0">Weight <span className="text-danger">*</span></label>
          <div className="unit-toggle-container">
            <button
              type="button"
              className={`unit-btn ${units.weight === 'kg' ? 'active' : ''}`}
              onClick={() => handleWeightUnitChange("kg")}
            >KG</button>
            <button
              type="button"
              className={`unit-btn ${units.weight === 'lbs' ? 'active' : ''}`}
              onClick={() => handleWeightUnitChange("lbs")}
            >LBS</button>
          </div>
        </div>
        <div className="input-group">
          <input
            type="text"
            disabled={!questionnaireData.dateOfBirth}
            className="form-control"
            placeholder={units.weight === 'kg' ? "75" : "165"}
            value={questionnaireData.weight}

            onChange={(e) => {
              let value = e.target.value
                .replace(/[^0-9.]/g, "")
                .replace(/(\..*?)\..*/g, "$1");


              if (units.weight === "kg") {
                if (value && parseFloat(value) > 500) {
                  value = "500"; // clamp to max
                }
              }

              handleQChange("weight", value);

              const ageInMonths = getAgeInMonths(questionnaireData.dateOfBirth);

              let weight = parseFloat(value);


              if (units.weight === "lbs") {
                weight = weight * 0.453592;
              }

              let errorMsg = "";

              if (ageInMonths !== null && ageInMonths <= 1) {
                if (!value) {
                  errorMsg = "Weight is required";
                } else if (weight < 2 || weight > 5) {
                  errorMsg = "For newborn, weight must be between 2–5 kg";
                }
              }

              setErrors((prev: any) => ({
                ...prev,
                weight: errorMsg,
              }));
            }}
          />
          <span className="input-group-text  fs-12 text-muted" style={{ width: '52px', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
            {units.weight}
          </span>
        </div>
        {errors.weight && <p className="text-danger mt-1 mb-0 fs-12">{errors.weight}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Current Location <span className="text-danger">*</span></label>
        <input type="text" className="form-control" placeholder="Country, State/Region" value={questionnaireData.currentLocation} onChange={(e) => handleQChange('currentLocation', e.target.value.trimStart())} />
        {errors.currentLocation && <p className="text-danger mt-1 mb-0 fs-12">{errors.currentLocation}</p>}
      </div>
      <div className="col-md-6">
        {/* <div className="form-group"> */}
        <label className="form-label">Blood Group <span className="text-danger">*</span></label>
        <select
          name="bloodGroup"
          value={questionnaireData.bloodGroup}
          onChange={(e) => handleQChange("bloodGroup", e.target.value)}
          className={`form-select`}
        >
          <option value="" disabled hidden>Select Blood Group</option>
          <option value="A+">A+</option>
          <option value="A-">A-</option>
          <option value="B+">B+</option>
          <option value="B-">B-</option>
          <option value="O+">O+</option>
          <option value="O-">O-</option>
          <option value="AB+">AB+</option>
          <option value="AB-">AB-</option>
        </select>
        {errors.bloodGroup && <p className="text-danger mt-1 mb-0 fs-12">{errors.bloodGroup}</p>}
      </div>

      {/* </div> */}
    </div>,

    // Step 1 - Emergency
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Emergency Contact Name <span className="text-danger">*</span></label>
        <input type="text" className="form-control" placeholder="Name" value={questionnaireData.emergencyContactName} onChange={(e) => handleQChange('emergencyContactName', e.target.value.trimStart())} />
        {errors.emergencyContactName && <p className="text-danger mt-1 mb-0 fs-12">{errors.emergencyContactName}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Relationship <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          styles={selectStyles}
          options={relationshipOptions}
          value={relationshipOptions.find((o: any) => o.value === questionnaireData.emergencyRelationship) || null}
          onChange={(val: any) => handleQChange("emergencyRelationship", val?.value || "")}
          placeholder="Select"
          menuPortalTarget={document.body}
        />
        {errors.emergencyRelationship && <p className="text-danger mt-1 mb-0 fs-12">{errors.emergencyRelationship}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Phone Number <span className="text-danger">*</span></label>
        <input type="tel" className="form-control" placeholder="Phone Number" value={questionnaireData.emergencyPhoneNumber} maxLength={10} onChange={(e) => {
          const value = e.target.value.replace(/\D/g, ""); // allow only numbers
          handleQChange("emergencyPhoneNumber", value);
        }} />
        {errors.emergencyPhoneNumber && <p className="text-danger mt-1 mb-0 fs-12">{errors.emergencyPhoneNumber}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">
          Can we contact this person in case of a serious health concern? <span className="text-danger">*</span>
        </label>
        <Select
           styles={selectStyles}
          classNamePrefix="react-select"
          options={yesNoOptions}
          value={yesNoOptions.find(o => o.value === questionnaireData.emergencyContactConsent)}
          onChange={(val: any) => handleQChange('emergencyContactConsent', val?.value)}
          placeholder="Select"
          menuPortalTarget={document.body}
        />
        {errors.emergencyContactConsent && <p className="text-danger mt-1 mb-0 fs-12">{errors.emergencyContactConsent}</p>}
      </div>
      {(questionnaireData.emergencyContactConsent === "true" || questionnaireData.emergencyContactConsent === "false") && (
        <div className="col-12 fade-in">
          <div
            className="p-3 rounded d-flex align-items-center"
            style={{
              backgroundColor:
                questionnaireData.emergencyContactConsent === "true"
                  ? "#eafaf1"
                  : "#fff3f3",
              borderLeft:
                questionnaireData.emergencyContactConsent === "true"
                  ? "4px solid #28a745"
                  : "4px solid #dc3545",
            }}
          >
            <p
              className="mb-0 small fw-medium d-flex align-items-center"
              style={{ color: "#32325d" }}
            >
              <CheckCircle
                className="me-2"
                style={{
                  fontSize: 18,
                  color:
                    questionnaireData.emergencyContactConsent === "true"
                      ? "#28a745"
                      : "#dc3545",
                }}
              />

              {questionnaireData.emergencyContactConsent === "true" &&
                "Yes, you may contact this person if there is a serious health issue."}

              {questionnaireData.emergencyContactConsent === "false" &&
                "No, please do not contact this person for any health-related emergency."}
            </p>
          </div>
        </div>
      )}
    </div>,

    // Step 2 - Health Overview
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label">Diagnosed Conditions</label>
        <Select
          styles={selectStyles}
          classNamePrefix="react-select"
          onInputChange={(val) => setConditionSearch(val)}
          options={conditionOptions}
          getOptionValue={(option: any) => option.id}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>
              {option.descriptions}
            </span>
          )}
          isMulti
          getOptionLabel={(option: any) => option.name}
          value={selectedConditions}
          onChange={(val: any) => {
            setSelectedConditions(val || []);
            handleQChange(
              "diagnosedConditionIds",
              val ? val.map((o: any) => o.id) : []
            );
          }}
          placeholder="Select conditions"
          menuPortalTarget={document.body}
        />
      </div>

      <div className="col-12">
        <label className="form-label">Allergies</label>
        <Select
          styles={selectStyles}

          classNamePrefix="react-select"
          isMulti
          getOptionLabel={(option: any) => option.name}
          onInputChange={(val) => setAllergySearch(val)}
          options={allergyOptions}
          getOptionValue={(option: any) => option.id}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>
              {option.descriptions}
            </span>
          )}
          value={selectedAllergies}
          onChange={(val: any) => {
            setSelectedAllergies(val || []);
            handleQChange(
              "allergyIds",
              val ? val.map((o: any) => o.id) : []
            );
          }}
          placeholder="Select allergies"
          menuPortalTarget={document.body}
        />
      </div>
    </div>,

    // Step 3 - Medications
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Currently taking medications? <span className="text-danger">*</span></label>
        <Select
          styles={selectStyles}
          classNamePrefix="react-select"
          options={yesNoOptions}
          value={yesNoOptions.find(o => o.value === questionnaireData.currentlyTakingMedications)}
          onChange={(val: any) => handleQChange('currentlyTakingMedications', val?.value)}
          placeholder="Select"
          menuPortalTarget={document.body}
        />
        {errors.currentlyTakingMedications && <p className="text-danger mt-1 mb-0 fs-12">{errors.currentlyTakingMedications}</p>}
      </div>

      <div className="col-md-6">
        <label className="form-label">Medication Names <span className="text-danger">*</span></label>


        <Select
           styles={selectStyles}
          classNamePrefix="react-select"
          onInputChange={(val) => setMedicationSearch(val)}
          options={medicationOptions}
          isMulti
          getOptionLabel={(option: any) => option.name}
          getOptionValue={(option: any) => option.id}

          value={selectedMedications}
          onChange={(val: any) => {
            setSelectedMedications(val || []);
            handleQChange(
              "patientMedicationIds",
              val ? val.map((o: any) => o.id) : []
            );
          }}
          placeholder="Medication name(s)"
          menuPortalTarget={document.body}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>
              {option.descriptions}
            </span>
          )}

        />
        {errors.patientMedicationIds && <p className="text-danger mt-1 mb-0 fs-12">{errors.patientMedicationIds}</p>}
      </div>

      <div className="col-12">
        <label className="form-label">
          Supplements / Non-prescription Remedies
        </label>
        <Select
           styles={selectStyles}
          classNamePrefix="react-select"
          onInputChange={(val) => setSupplementSearch(val)}
          options={supplementOptions}
          isMulti
          getOptionLabel={(option: any) => option.name}
          getOptionValue={(option: any) => option.id}
          value={selectedSupplements}
          onChange={(val: any) => {
            setSelectedSupplements(val || []);
            handleQChange(
              "supplementIds",
              val ? val.map((o: any) => o.id) : []
            );
          }}
          placeholder="Select supplements"
          menuPortalTarget={document.body}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>
              {option.descriptions}
            </span>
          )}

        />
      </div>
    </div>,

    // Step 4 - Family History
    <div className="col-12">
      <label className="form-label">
        Family History (Close family conditions)
      </label>
      <Select
        styles={selectStyles}

        classNamePrefix="react-select"
        isMulti
        getOptionLabel={(option: any) => option.name}
        getOptionValue={(option: any) => option.id}
        value={selectedFamilyHistory}

        onChange={(val: any) => {
          setSelectedFamilyHistory(val || []);
          handleQChange(
            "familyHistoryDiseaseIds",
            val ? val.map((o: any) => o.id) : []
          );
        }}
        onInputChange={(val) => setFamilySearch(val)}
        options={familyOptions}
        placeholder="Select conditions"
        menuPortalTarget={document.body}




        formatOptionLabel={(option: any) => (
          <span title={option.descriptions}>
            {option.descriptions}
          </span>
        )}

      />
    </div>,

    // Step 5 - Goals
    <div className="col-12">
      <label className="form-label">What would you like help with most?</label>


      <textarea
        className="form-control"
        rows={4}
        placeholder="Enter Goals"
        value={questionnaireData.goals} onChange={(e) => handleQChange('goals', e.target.value.trimStart())}
      />
      {errors.goals && <p className="text-danger mt-1 mb-0 fs-12">{errors.goals}</p>}

    </div>,

    // Step 6 - Final Input
    <div className="col-12">
      <label className="form-label">
        Is there anything you'd like us to know about your health right now?
      </label>
      <textarea
        className="form-control"
        rows={4}
        placeholder="Type"
        value={questionnaireData.finalInput}
        onChange={(e) => handleQChange('finalInput', e.target.value.trimStart())}
      />
    </div>,
  ];


  return (
    <div className="content-wrapper">
      <div className="container-full table-padding">

        <div className="page-header">


          <div className="breadcrumb">
            <h3 className="page-title">Family Members</h3>
            <span className="customize-breadcrumb">
              <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
              <span className="separator"> - </span>
              <span className="current">Family Members</span>
            </span>

          </div>
        </div>
        <div className="family-card">

          {/* Header */}
          <div className="family-header">
            {/* LEFT SIDE */}
            <div className="left-side">
              <p className="title">My Family Members</p>
              <Input.Search placeholder="Search By Name" className="search-input" value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
            </div>

            {/* RIGHT SIDE */}
            <div className="right-side">
              <Space>
                <Button className="btn-add" icon={<PlusOutlined />} title={"Add Family Member"} onClick={() => {
                  setEditingRecord(null);
                  setShowModal(true); setFormData({
                    firstName: "",
                    middleName: "",
                    lastName: "",
                    dateOfBirth: "",
                    bloodGroup: "",
                    status: true,
                    relationshipType: "SPOUSE",
                    gender: "MALE",
                    sexAssignedAtBirth: "MAN",
                    phoneNumber: "",
                    email: ""
                  })
                }} />
                {/* <Button className="btn-download" icon={<DownloadOutlined />} /> */}
              </Space>
            </div>
          </div>


          {/* Table */}
          <Table
            columns={columns}
            dataSource={paginatedDataSource}
            pagination={false}
            className="family-table"
            scroll={{ x: "max-content" }}
          />

          {/* Footer */}
          <div className="family-footer">
            <span>Total {dataSource.length}</span>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={dataSource.length}
              showSizeChanger
              pageSizeOptions={["5", "10", "20", "50"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              onShowSizeChange={(page, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
            />
          </div>

        </div>
      </div>

      <Dialog
        open={showModal}
        maxWidth="md"
        fullWidth
        className="auth-modal glass-modal"
        onClose={() => {
          setShowModal(false);
          setEditingRecord(null);
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(5px)",
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 3,
          pb: 5,
        }}>
          <h3 style={{ margin: 0 }}>{editingRecord ? "Edit Family Member" : "New Family Member"}</h3>
          <IconButton
            onClick={() => setShowModal(false)}
            sx={{ color: "red" }} // red color
          >
            <CloseIcon sx={{ color: "gray", "&:hover": { color: "darkred" } }} />
          </IconButton>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent className="px-40 pb-30">
          <div className="row g-3">

            {/* ROW 1 */}
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">First Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                  placeholder="First Name"
                />
                {errors.firstName && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.firstName}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Middle Name"
                />
              </div>
            </div>

            {/* ROW 2 */}
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Last Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                  placeholder="Last Name"
                />
                {errors.lastName && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}


                  onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    const maxDate = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);

                    if (selectedDate > maxDate) {
                      setErrors((prev: any) => ({
                        ...prev,
                        dateOfBirth: "Future dates are not allowed",
                      }));
                      return;
                    }

                    setErrors((prev: any) => ({
                      ...prev,
                      dateOfBirth: "",
                    }));

                    setFormData((prev: any) => ({
                      ...prev,
                      dateOfBirth: e.target.value,   // ✅ correct
                    }));
                  }}
                  max={new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split("T")[0]}
                  className={`form-control ${errors.dateOfBirth ? "is-invalid" : ""}`}
                />
                {errors.dateOfBirth && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.dateOfBirth}
                  </div>
                )}
              </div>
            </div>

            {/* ROW 3 */}
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Blood Group <span className="text-danger">*</span></label>
                <Select
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  options={[
                    { value: "A+", label: "A+" },
                    { value: "A-", label: "A-" },
                    { value: "B+", label: "B+" },
                    { value: "B-", label: "B-" },
                    { value: "O+", label: "O+" },
                    { value: "O-", label: "O-" },
                    { value: "AB+", label: "AB+" },
                    { value: "AB-", label: "AB-" },
                  ]}
                  value={formData.bloodGroup ? { value: formData.bloodGroup, label: formData.bloodGroup } : null}
                  onChange={(val: any) => handleFamilyChange("bloodGroup", val?.value || "")}
                  placeholder="Select Blood Group"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {errors.bloodGroup && <div className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors.bloodGroup}</div>}
              </div>
            </div>



            {/* ROW 4 */}
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Relation <span className="text-danger">*</span></label>
                <Select
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  options={relationshipOptions}
                  value={relationshipOptions.find((o: any) => o.value === formData.relationshipType) || null}
                  onChange={(val: any) => handleFamilyChange("relationshipType", val?.value || "")}
                  placeholder="Select Relation"
                  menuPortalTarget={document.body}
                />
                {errors.relationshipType && <div className="text-danger mt-1" style={{ fontSize: "12px" }}>{errors.relationshipType}</div>}
              </div>
            </div>
            {/* 


            {/* ROW 5 */}
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  maxLength={10}
                  className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                  placeholder="Phone Number"
                />
                {errors.phoneNumber && (
                  <div className="text-danger mt-1">{errors.phoneNumber}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Email (optional)"
                />
                {errors.email && (
                  <div className="text-danger mt-1">{errors.email}</div>
                )}
              </div>

            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Status <span className="text-danger">*</span></label>
                <select
                  name="status"
                  value="true"
                  disabled
                  className="form-select"
                >
                  <option value="true">Active</option>
                </select>
              </div>
            </div>

          </div>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions className="pb-30 px-40 d-flex justify-content-end gap-2">
          <button
            className="btn btn-warning"
            onClick={() => {
              setShowModal(false);
              setEditingRecord(null);
            }}
            disabled={isLoading}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSaveFamilyMember}
            disabled={isLoading || isUpdateLoading}
          >
            {(isLoading || isUpdateLoading) ? "Saving..." : (editingRecord ? "Update" : "Save")}
          </button>
        </DialogActions>
      </Dialog>





      <Dialog
        open={showModal1}
        maxWidth="md"
        fullWidth
        className="auth-modal glass-modal"
        onClose={() => setShowModal1(false)}
        BackdropProps={{
          style: { backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(5px)" },
        }}
      >

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 3,
            pb: 5,
          }}
        >
          <h3 style={{ margin: 0 }}>Initial Health Questionnaire</h3>
          <IconButton
            onClick={() => setShowModal1(false)}
            sx={{ color: "red" }} // red color
          >
            <CloseIcon sx={{ color: "gray", "&:hover": { color: "darkred" } }} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="px-4 pb-3">
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                borderColor: "#5156be",
              },
              "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: "#5156be",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form className="mt-3">{stepContents[activeStep]}</form>
        </DialogContent>

        <DialogActions className="pb-3 px-4 d-flex justify-content-end">

          <button
            className="btn btn-warning"
            disabled={activeStep === 0} onClick={handleBack}
          >
            Back
          </button>

          {activeStep === steps.length - 1 ? (

            <button className="btn btn-primary" onClick={handleSaveQuestionnaire} disabled={isPostLoading}>
              {isPostLoading ? "Saving..." : "Save"}
            </button>

          ) : (

            <button className="btn btn-primary" onClick={handleNext}>
              Next
            </button>
          )}



        </DialogActions>
      </Dialog>


      <Dialog
        open={showConfirm}
        maxWidth="sm"
        fullWidth
        className="auth-modal glass-modal"
        onClose={() => setShowConfirm(false)}
        BackdropProps={{
          style: { backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(5px)" },
        }}
      >
        <DialogTitle className="pt-30 pb-10 text-center">
          <h2>Family Member Added Successfully!</h2>
        </DialogTitle>

        <DialogContent className="px-40 pb-30 text-center">
          <p className="mb-3">
            Please note: To provide complete information, we recommend filling out the health questionnaire form for this family member.
          </p>
          <p className="text-muted fs-14">
            Would you like to fill the questionnaire now?
          </p>
        </DialogContent>

        <DialogActions className="pb-30 px-40 d-flex justify-content-center gap-3">
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowModal(false)
              setShowConfirm(false);
              setShowModal1(true);
            }}
          >
            Yes
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowConfirm(false);
              setShowModal(false);
              setShowModal1(false);
            }}
          >
            No
          </button>
        </DialogActions>
      </Dialog>



    </div>


  );
};

export default PatientFamilyMember;
